import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Typography,
  Table,
  Divider,
  Row,
  Col,
  Tag,
  Button,
  Space,
} from "antd";
import {
  FileTextOutlined,
  DownloadOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { AppContext } from "../context/AppContext";
import jsPDF from "jspdf";
import "jspdf-autotable";
import axios from "axios";

const { Title, Text } = Typography;

const BusinessQuotation = ({ planData, onGeneratePDF }) => {
  const { user, company } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);

  // Quotation details
  const quotationNumber = `QUO-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  const quotationDate = new Date().toLocaleDateString();
  const validUntil = new Date(
    Date.now() + 30 * 24 * 60 * 60 * 1000
  ).toLocaleDateString(); // Valid for 30 days

  useEffect(() => {
    fetchBusinessPlans();
  }, []);

  const fetchBusinessPlans = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        "https://zedlenders.pickmesms.com/items/plans?filter[type][_eq]=business",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const sortedPlans = response.data.data.sort(
        (a, b) => a.months - b.months
      );
      setPlans(sortedPlans);
    } catch (error) {
      console.error("Error fetching business plans:", error);
    }
  };

  const calculatePlanPricing = (plan) => {
    // plan.price is the subtotal (before discount)
    const subtotal = plan.price;
    const discount = (subtotal * plan.discount) / 100;
    const total = subtotal - discount; // Total Due Today = Subtotal - Discount
    const effectiveMonthlyPrice = total / plan.months;

    return {
      subtotal,
      discount,
      total,
      effectiveMonthlyPrice,
    };
  };

  const generateQuotationData = () => {
    if (!plans.length) return [];

    return plans.map((plan) => {
      const pricing = calculatePlanPricing(plan);
      return {
        key: plan.id,
        description: `Kwachaplus Business Plan - ${plan.cycle} (${plan.months} month${plan.months > 1 ? "s" : ""})`,
        quantity: 1,
        basePrice: pricing.total, // This is what shows as "Base Price" in planCheckout
        subtotal: pricing.subtotal, // Before discount
        discount: pricing.discount,
        total: pricing.total, // Final price after discount
        cycle: plan.cycle,
        months: plan.months,
        discountPercent: plan.discount,
        monthlyPrice: pricing.monthlyPrice,
        effectiveMonthlyPrice: pricing.effectiveMonthlyPrice,
      };
    });
  };

  const quotationItems = generateQuotationData();

  const columns = [
    {
      title: "Plan Description",
      dataIndex: "description",
      key: "description",
      width: "50%",
      render: (text, record) => (
        <div>
          <Text strong style={{ fontSize: "13px" }}>
            {text}
          </Text>
          {record.discountPercent > 0 && (
            <div>
              <Tag color="green" size="small">
                Save {record.discountPercent}%
              </Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Duration",
      dataIndex: "months",
      key: "months",
      align: "center",
      render: (months) => `${months} month${months > 1 ? "s" : ""}`,
    },
    {
      title: "Price",
      dataIndex: "total",
      key: "total",
      align: "right",
      width: "25%",
      render: (total, record) => (
        <div>
          <Text strong style={{ fontSize: "14px", color: "#00796b" }}>
            K
            {total.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
          <div>
            <Text type="secondary" style={{ fontSize: "11px" }}>
              K
              {record.effectiveMonthlyPrice.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              /month effective
            </Text>
          </div>
        </div>
      ),
    },
  ];

  const generatePDF = async () => {
    setLoading(true);
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;

      // Company Header
      pdf.setFontSize(20);
      pdf.setFont(undefined, "bold");
      pdf.text("Kwachaplus", 20, 30);

      pdf.setFontSize(12);
      pdf.setFont(undefined, "normal");
      pdf.text("Loan Management System", 20, 40);
      pdf.text("Lusaka, Zambia", 20, 50);

      // Quotation Title
      pdf.setFontSize(24);
      pdf.setFont(undefined, "bold");
      pdf.text("QUOTATION", pageWidth - 20, 30, { align: "right" });

      // Quotation Details
      pdf.setFontSize(10);
      pdf.setFont(undefined, "normal");
      pdf.text(`Quotation #: ${quotationNumber}`, pageWidth - 20, 45, {
        align: "right",
      });
      pdf.text(`Date: ${quotationDate}`, pageWidth - 20, 55, {
        align: "right",
      });
      pdf.text(`Valid Until: ${validUntil}`, pageWidth - 20, 65, {
        align: "right",
      });

      // Bill To Section
      pdf.setFontSize(14);
      pdf.setFont(undefined, "bold");
      pdf.text("Bill To:", 20, 80);

      pdf.setFontSize(11);
      pdf.setFont(undefined, "normal");
      // pdf.text(`${company?.company_name || "Company Name"}`, 20, 95);
      pdf.text(`Folla Finance Ltd`, 20, 95);
      //pdf.text(`${user?.first_name || ""} ${user?.last_name || ""}`, 20, 105);
      //pdf.text(`${user?.email || "email@example.com"}`, 20, 115);

      // Table Header
      const tableData = quotationItems.map((item) => [
        item.description,
        `${item.months} month${item.months > 1 ? "s" : ""}`,
        `K${item.total.toLocaleString("en-US", { minimumFractionDigits: 2 })} total`,
        `K${item.effectiveMonthlyPrice.toLocaleString("en-US", { minimumFractionDigits: 2 })}/month effective`,
      ]);

      pdf.autoTable({
        head: [
          ["Plan Description", "Duration", "Total Price", "Effective Monthly"],
        ],
        body: tableData,
        startY: 110,
        theme: "grid",
        headStyles: { fillColor: [0, 121, 107] },
        styles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 70 },
          1: { cellWidth: 30, halign: "center" },
          2: { cellWidth: 45, halign: "right" },
          3: { cellWidth: 40, halign: "right" },
        },
      });

      // Reference Clients Section
      const finalY = pdf.lastAutoTable.finalY + 20;
      pdf.setFontSize(12);
      pdf.setFont(undefined, "bold");
      pdf.text("Reference Clients:", 20, finalY);

      pdf.setFontSize(9);
      pdf.setFont(undefined, "normal");
      const referenceClients = [
        "• Brolliance Limited - Contact: Frank Lungu, Tel: +260 973 23 68 30",
        "• Uptake Resources Limited - Contact: Sahid Kazembe, Tel: +260 966 30 57 47",
        "• Achire Business Support - Contact: Isililo Mzyece, Tel: +260 977 75 42 99",
        "• KSM Consultants - Contact: Kelvin Sokuni, Tel: +260 977 75 00 71",
      ];

      referenceClients.forEach((client, index) => {
        pdf.text(client, 25, finalY + 15 + index * 10);
      });

      // Footer
      pdf.setFontSize(9);
      pdf.setFont(undefined, "normal");
      const footerY = pdf.internal.pageSize.height - 30;
      pdf.text(
        "Quotation Terms: This quotation is valid for 30 days from the date above.",
        20,
        footerY
      );
      pdf.text(
        "Implementation: Service activation within 24 hours of payment confirmation.",
        20,
        footerY + 7
      );
      pdf.text(
        "Thank you for considering Kwachaplus Business Plan!",
        20,
        footerY + 14
      );

      // Features included
      pdf.setFontSize(8);
      pdf.text(
        "Features included: 24/7 Support • PCI Compliance • Bank-Grade Security • Cancel Anytime",
        20,
        footerY + 20
      );

      // Save the PDF
      pdf.save(`Kwachaplus_Business_Quotation_${quotationNumber}.pdf`);

      if (onGeneratePDF) {
        onGeneratePDF(pdf);
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setLoading(false);
    }
  };

  const printQuotation = () => {
    window.print();
  };

  if (!plans.length) {
    return (
      <Card loading={true}>
        <Text>Loading Business plan details...</Text>
      </Card>
    );
  }

  return (
    <div
      className="quotation-container"
      style={{ maxWidth: "900px", margin: "0 auto", padding: "20px" }}
    >
      <Card>
        {/* Quotation Header */}
        <div style={{ marginBottom: "30px" }}>
          <Row justify="space-between" align="top">
            <Col>
              <Title
                level={2}
                style={{ marginBottom: "5px", color: "#00796b" }}
              >
                Kwachaplus
              </Title>
              <Text type="secondary">Loan Management System</Text>
              <br />
              <Text type="secondary">Lusaka, Zambia</Text>
            </Col>
            <Col style={{ textAlign: "right" }}>
              <Title level={1} style={{ marginBottom: "10px" }}>
                QUOTATION
              </Title>
              <div>
                <Text strong>Quotation #: </Text>
                <Text>{quotationNumber}</Text>
              </div>
              <div>
                <Text strong>Date: </Text>
                <Text>{quotationDate}</Text>
              </div>
              <div>
                <Text strong>Valid Until: </Text>
                <Text>{validUntil}</Text>
              </div>
            </Col>
          </Row>
        </div>

        <Divider />

        {/* Quote To Section */}
        <div style={{ marginBottom: "30px" }}>
          <Title level={4}>Quote To:</Title>
          <div>
            {/* <Text strong>{company?.company_name || "Company Name"}</Text> */}
            <Text>Folla Finance Ltd</Text>
          </div>
          <div>
            <Text>
              {user?.first_name || ""} {user?.last_name || ""}
            </Text>
          </div>
          <div>
            <Text>{user?.email || "email@example.com"}</Text>
          </div>
        </div>

        <Divider />

        {/* Quotation Items Table */}
        <Table
          columns={columns}
          dataSource={quotationItems}
          pagination={false}
          size="middle"
          style={{ marginBottom: "30px" }}
        />

        <Divider />

        {/* Order Summary for Each Plan */}
        <div style={{ marginBottom: "30px" }}>
          <Title level={4}>Pricing Breakdown</Title>
          <Row gutter={[16, 16]}>
            {quotationItems.map((item) => (
              <Col xs={24} md={12} lg={8} key={item.key}>
                <Card
                  size="small"
                  title={
                    item.cycle.charAt(0).toUpperCase() + item.cycle.slice(1)
                  }
                  style={{ height: "100%" }}
                >
                  <div style={{ fontSize: "12px" }}>
                    <div style={{ marginBottom: "8px" }}>
                      <Text>Base Price: </Text>
                      <Text strong>
                        K
                        {item.subtotal.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Text>
                    </div>

                    <div style={{ marginBottom: "8px" }}>
                      <Text>Duration: </Text>
                      <Text>
                        {item.months} month{item.months > 1 ? "s" : ""}
                      </Text>
                    </div>

                    <div style={{ marginBottom: "8px" }}>
                      <Text>Subtotal: </Text>
                      <Text>
                        K
                        {item.subtotal.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Text>
                    </div>

                    {item.discount > 0 && (
                      <div style={{ marginBottom: "8px" }}>
                        <Text>Discount: </Text>
                        <Text style={{ color: "#52c41a" }}>
                          -K
                          {item.discount.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Text>
                      </div>
                    )}

                    <Divider style={{ margin: "8px 0" }} />

                    <div>
                      <Text strong>Total Due Today: </Text>
                      <Text
                        strong
                        style={{ color: "#00796b", fontSize: "14px" }}
                      >
                        K
                        {item.total.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Text>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        <Divider />

        {/* Actions */}
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <Space size="large">
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              size="large"
              loading={loading}
              onClick={generatePDF}
            >
              Download PDF
            </Button>
            <Button
              icon={<PrinterOutlined />}
              size="large"
              onClick={printQuotation}
            >
              Print Quotation
            </Button>
          </Space>
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: "30px" }}>
          <Text type="secondary">
            <strong>Quotation Terms:</strong> This quotation is valid for 30
            days from the date above.
          </Text>
          <br />
          <Text type="secondary">
            <strong>Implementation:</strong> Service activation within 24 hours
            of payment confirmation.
          </Text>
          <br />
          <Text type="secondary">
            Thank you for considering Kwachaplus Business Plan!
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: "11px" }}>
            Features included: 24/7 Support • PCI Compliance • Bank-Grade
            Security • Cancel Anytime
          </Text>
        </div>
      </Card>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .quotation-container {
            padding: 0;
            margin: 0;
            max-width: none;
          }
          .ant-card {
            border: none;
            box-shadow: none;
          }
          .ant-btn {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BusinessQuotation;
